/* eslint-disable @typescript-eslint/camelcase */
import i18n from 'i18n';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import {
  BlockedEnvironmentsFields,
  EnvironmentHealthFields,
  InstanceStatusFields,
  InstancesWithSessionTimeoutFields,
} from 'modules/monitor/interfaces';
import palette from 'theme/palette';

export enum MonitorCardsEnum {
  environmentMissing = 'environmentMissing',
  environmentBlock = 'environmentBlock',
  instanceProblem = 'instanceProblem',
  instanceSessionTimeoutProblem = 'instanceSessionTimeoutProblem',
  environmentsWithFailedZeroTrust = 'environmentsWithFailedZeroTrust',
}

export const getPDFHeaders = (card: MonitorCardsEnum) => {
  const headers = {
    [MonitorCardsEnum.environmentMissing]: [
      i18n.t('MONITOR.PARTNER_NAME'),
      i18n.t('MONITOR.ENVIRONMENT_NAME'),
      i18n.t('MONITOR.ROBO_TYPE'),
      i18n.t('MONITOR.STATUS'),
      i18n.t('MONITOR.INSTANCES'),
      i18n.t('MONITOR.TEMPERATURE'),
      i18n.t('MONITOR.CONNECTED'),
      i18n.t('MONITOR.LICENSES_TOTAL'),
      i18n.t('MONITOR.VACANCIES'),
    ],
    [MonitorCardsEnum.instanceProblem]: [
      i18n.t('MONITOR.PARTNER_NAME'),
      i18n.t('MONITOR.ENVIRONMENT_NAME'),
      i18n.t('MONITOR.FLAVOR'),
      i18n.t('MONITOR.ZONE'),
      i18n.t('MONITOR.COUNT'),
    ],
    [MonitorCardsEnum.environmentBlock]: [
      i18n.t('MONITOR.PARTNER_NAME'),
      i18n.t('MONITOR.ENVIRONMENT_NAME'),
      i18n.t('MONITOR.PROVIDER'),
      i18n.t('MONITOR.SEC_PROCESSED'),
    ],
    [MonitorCardsEnum.instanceSessionTimeoutProblem]: [
      i18n.t('INSTANCE_ID_KEY'),
      i18n.t('MONITOR.PARTNER_NAME'),
      i18n.t('MONITOR.ENVIRONMENT_NAME'),
      i18n.t('MONITOR.FLAVOR'),
    ],
  };

  return headers[card];
};

export const getPDFTableBody = (
  data:
    | BlockedEnvironmentsFields
    | EnvironmentHealthFields
    | InstanceStatusFields
    | InstancesWithSessionTimeoutFields,
  card: MonitorCardsEnum
) => {
  if (card === MonitorCardsEnum.environmentMissing) {
    return (data as EnvironmentHealthFields)?.results?.map(item => [
      item?.partner?.name,
      item?.environment?.name,
      item?.environment?.publication_type,
      item?.status,
      item?.avg_temperature,
      item?.connections,
      item?.total_licenses,
      item?.vacancies,
    ]);
  }
  if (card === MonitorCardsEnum.instanceProblem) {
    return (data as InstanceStatusFields)?.results?.map(item => [
      item?.partner?.name,
      item?.environment?.name,
      item?.flavor,
      item?.zone,
      item?.status_quantity,
    ]);
  }
  if (card === MonitorCardsEnum.environmentBlock) {
    return (data as BlockedEnvironmentsFields)?.results?.map(item => [
      item?.partner?.name,
      item?.environment?.name,
      item?.environment?.provider,
      item?.elapsed_blocking_time,
    ]);
  }
  if (card === MonitorCardsEnum.instanceSessionTimeoutProblem) {
    return (data as InstancesWithSessionTimeoutFields)?.results?.map(item => [
      item?.instance_id,
      item?.partner?.name,
      item?.environment?.name,
      item?.flavor,
    ]);
  }
  return [];
};

const setPDFData = (
  doc: jsPDF,
  creation_date: Date,
  card: MonitorCardsEnum
) => {
  const titles = {
    [MonitorCardsEnum.environmentMissing]: i18n.t(
      'MONITOR.CARD_ENVIRONMENT_HEALTH'
    ),
    [MonitorCardsEnum.instanceProblem]: i18n.t('MONITOR.CARD_INSTANCES'),
    [MonitorCardsEnum.environmentBlock]: i18n.t(
      'MONITOR.CARD_BLOCKED_ENVIRONMENTS'
    ),
    [MonitorCardsEnum.instanceSessionTimeoutProblem]: i18n.t(
      'MONITOR.INSTANCES_WITH_SESSION_TIMEOUT_TITLE'
    ),
  };

  doc.setFontSize(14);
  doc.text(
    [
      '',
      `${i18n.t('PDF_GENERATED_AT_KEY')}: ${creation_date.toLocaleString(
        'pt-BR',
        {
          hour12: false,
        }
      )}`,
      '',
      `${titles[card]}:`,
    ],
    10,
    10,
    { lineHeightFactor: 1.5, maxWidth: 200 }
  );
};

const addTableSection = (doc: jsPDF, header: string[], body: any[]) => {
  autoTable(doc, {
    head: [header],
    margin: { top: 50 },
    styles: { lineWidth: 0.3, lineColor: palette.borderGray, fontSize: 11 },
    headStyles: {
      fillColor: palette.deepBlue,
      textColor: palette.offWhite,
      fontSize: 11,
    },
    body,
  });
};

export const savePDF = (
  doc: jsPDF,
  creation_date: Date,
  card: MonitorCardsEnum
) => {
  const titles = {
    [MonitorCardsEnum.environmentMissing]: 'environment-health',
    [MonitorCardsEnum.instanceProblem]: 'instances-analysis',
    [MonitorCardsEnum.environmentBlock]: 'blocked-environments',
    [MonitorCardsEnum.instanceSessionTimeoutProblem]:
      'instances-with-session-timeout-analysis',
  };
  const formattedDate = moment(creation_date).format('YYYYMMDD_HHmmss');
  doc.save(`${titles[card]}_${formattedDate}`);
};

export const generatePDF = (
  data:
    | BlockedEnvironmentsFields
    | EnvironmentHealthFields
    | InstanceStatusFields
    | InstancesWithSessionTimeoutFields,

  card: MonitorCardsEnum
) => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF();
  const now = new Date();
  const headers = getPDFHeaders(card);
  const tableBody = getPDFTableBody(data, card);

  setPDFData(doc, now, card);
  addTableSection(doc, headers, tableBody);
  savePDF(doc, now, card);
};
